/**
 * Highlight service for the `show` MCP tool.
 *
 * Stores the active highlight request as a signal that the overlay component reads.
 * One overlay at a time — a new highlight replaces the previous one.
 * Auto-clears after SHOW_OVERLAY_DURATION_MS; the UI layer can also call clear() on builder click.
 * A mark to press is the exception: it stays until its wait ends.
 */

/** How long the highlight overlay stays visible (ms). Exported so tests can assert it. */
export const SHOW_OVERLAY_DURATION_MS = 6000;

export interface ShowTarget {
  // 'button' is the confirm lane's: the show tool's schema doesn't offer it, so Claude can't aim at one.
  kind: 'component' | 'field' | 'area' | 'button';
  id: string;
}

/** The screen areas the editor tags with data-mcp-area; the tool offers exactly these. */
export const KNOWN_AREAS = ['header', 'sidebar', 'canvas', 'inspector'] as const;
export type KnownArea = typeof KNOWN_AREAS[number];

/** The attribute each kind of target is tagged with, so the tool and the overlay look for the same element. */
export function targetSelector(target: ShowTarget): string {
  const id = CSS.escape(target.id);
  switch (target.kind) {
    case 'field': return `[data-mcp-field="${id}"]`;
    case 'component': return `[data-mcp-component="${id}"]`;
    case 'area': return `[data-mcp-area="${id}"]`;
    case 'button': return `[data-mcp-button="${id}"]`;
  }
}

export interface ActiveHighlight {
  target: ShowTarget;
  note?: string;
  /** Epoch ms when auto-clear is scheduled; a mark to press has none. */
  clearedAt: number;
  /** A button waiting for the builder's click: it stays until the wait ends and lets the click through. */
  sticky?: boolean;
}

/**
 * Minimal highlight service — no Angular DI so the tool factory can use it in tests
 * with a plain instance. The real Angular wrapper can extend or delegate to this.
 */
export class ShowHighlightService {
  private _activeHighlight: ActiveHighlight | null = null;
  private _timer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<() => void>();
  private _mark: ActiveHighlight | null = null;

  get activeHighlight(): ActiveHighlight | null {
    return this._activeHighlight ?? this._mark;
  }

  /** Calls `listener` whenever a highlight is shown or goes away, including the auto-clear; returns an unsubscribe. */
  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private changed(): void {
    for (const listener of this.listeners) listener();
  }

  highlight(target: ShowTarget, note?: string): void {
    // Cancel any pending auto-clear from a previous highlight.
    if (this._timer !== null) {
      clearTimeout(this._timer);
      this._timer = null;
    }

    const clearedAt = Date.now() + SHOW_OVERLAY_DURATION_MS;
    this._activeHighlight = { target, clearedAt, ...(note ? { note } : {}) };

    this._timer = setTimeout(() => {
      this._activeHighlight = null;
      this._timer = null;
      this.changed();
    }, SHOW_OVERLAY_DURATION_MS);
    this.changed();
  }

  /** Marks what the builder is being asked to press. A `show` meanwhile goes over it for its few seconds and the mark comes back. */
  markToPress(target: ShowTarget, note: string): void {
    this._mark = { target, note, clearedAt: Number.POSITIVE_INFINITY, sticky: true };
    this.changed();
  }

  unmark(): void {
    if (this._mark === null) return;
    this._mark = null;
    this.changed();
  }

  clear(): void {
    if (this._timer !== null) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    const had = this._activeHighlight !== null;
    this._activeHighlight = null;
    if (had) this.changed();
  }
}
